import React, { useState, useEffect, useRef } from 'react';
import { 
    XMarkIcon, 
    UserIcon, 
    DocumentTextIcon, 
    FolderIcon, 
    PlusIcon, 
    HeartIcon, 
    EnvelopeIcon, 
    PhoneIcon, 
    MapPinIcon, 
    IdentificationIcon, 
    CalendarIcon, 
    UserGroupIcon,
    HomeIcon,
    ShieldCheckIcon 
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { PersonalDetail } from '../../types/personalDetails';
import PersonalDetailsService from '../../services/personalDetails.service';
import { toast } from 'react-hot-toast';
import Button from '../common/Button';
import { useCategories } from '../../contexts/CategoryContext';
import { decryptKeyData } from '../../utils/encryption';
import { secureStore } from '../../utils/secureStore';
import { useAppSelector } from '../../hooks/useRedux';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { MultiSelect } from 'react-multi-select-component';

interface Option {
    label: string;
    value: string;
    disabled?: boolean;
    isShared?: boolean;
    sharedKey?: string;
}
interface FormData {
    name: string;
    title: string;
    encrypted_first_name: string;
    encrypted_middle_name: string;
    encrypted_last_name: string;
    encrypted_username: string;
    gender: string;
    birthday: string;
    encrypted_email_address: string;
    encrypted_phone: string;
    encrypted_address_1: string;
    encrypted_address_2: string;
    encrypted_city_town: string;
    encrypted_state: string;
    encrypted_zip_postal_code: string;
    encrypted_country: string;
    encrypted_timezone: string;
    encrypted_notes: string;
    category_id: string;
    favourite: boolean;
    shared_key: string;
    category_ids: string[];
    remove_category_ids: string[];
}
interface PersonalDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    personal?: PersonalDetail;
    isSharedPersonal?: boolean;
    permissionLevel?: string;
    sharedKey?: string;
    onSuccessfulUpdate?: () => Promise<void> | void;
    categoryId?: string;
    isAdminView?: boolean;
}

const PersonalDetailsModal: React.FC<PersonalDetailsModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    personal,
    isSharedPersonal = false,
    permissionLevel = 'read',
    sharedKey,
    onSuccessfulUpdate,
    categoryId,
    isAdminView = false
}) => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        title: '',
        encrypted_first_name: '',
        encrypted_middle_name: '',
        encrypted_last_name: '',
        encrypted_username: '',
        gender: '',
        birthday: '',
        encrypted_email_address: '',
        encrypted_phone: '',
        encrypted_address_1: '',
        encrypted_address_2: '',
        encrypted_city_town: '',
        encrypted_state: '',
        encrypted_zip_postal_code: '',
        encrypted_country: '',
        encrypted_timezone: '',
        encrypted_notes: '',
        category_id: categoryId || '',
        favourite: false,
        shared_key: '',
        category_ids: [],
        remove_category_ids: []
    });

    const [loading, setLoading] = useState(false);
    const { categories, refreshCategories, createCategory } = useCategories();
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [creatingCategoryLoading, setCreatingCategoryLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<any>(null);
    const { role_id, team_id } = useAppSelector(state => state.auth);
    const { activeWorkspace } = useWorkspace();
    const initialCategoriesRef = useRef<string[]>([]);

    const categoryOptions: Option[] = categories.filter(cat => !(cat.permission_level === 'read')).map(cat => ({
        label: cat.name,
        value: cat.id,
        isShared: cat.is_shared,
        sharedKey: cat.shared_key
    }));

    const initializeSelectedOptions = (): Option[] => {
        if (personal?.categories && personal.categories.length > 0) {
            const validCategories = personal.categories.filter(personalCategory => 
                categories.some((userCategory:any) => userCategory.id === personalCategory.id)
            );
            return validCategories.map(cat => ({
                label: cat.name,
                value: cat.id,
                isShared: cat.is_shared,
                sharedKey: cat.shared_key
            }));
        } else if (personal?.category_id) {
            const category = categories.find(c => c.id === personal.category_id);
            if (category) {
                return [{
                    label: category.name,
                    value: category.id,
                    isShared: category.is_shared,
                    sharedKey: category.shared_key
                }];
            }
        } else if (categoryId) {
            const category = categories.find(c => c.id === categoryId);
            if (category) {
                return [{
                    label: category.name,
                    value: category.id,
                    isShared: category.is_shared,
                    sharedKey: category.shared_key
                }];
            }
        }
        return [];
    };
    const [selectedOptions, setSelectedOptions] = useState<Option[]>(initializeSelectedOptions());
    // useEffect(() => {
    //     if (!isOpen) {
    //         // Reset form when closing
    //         setFormData({
    //             title: '',
    //             encrypted_first_name: '',
    //             encrypted_middle_name: '',
    //             encrypted_last_name: '',
    //             encrypted_username: '',
    //             gender: '',
    //             birthday: '',
    //             encrypted_email_address: '',
    //             encrypted_phone: '',
    //             encrypted_address_1: '',
    //             encrypted_address_2: '',
    //             encrypted_city_town: '',
    //             encrypted_state: '',
    //             encrypted_zip_postal_code: '',
    //             encrypted_country: '',
    //             encrypted_timezone: '',
    //             encrypted_notes: '',
    //             category_id: '',
    //             name: '',
    //             favourite: false,
    //             shared_key: ''
    //         });
    //     } else if (personal) {
    //         // Edit existing personal details
    //         setFormData({
    //             title: personal.title || '',
    //             encrypted_first_name: personal.encrypted_first_name,
    //             encrypted_middle_name: personal.encrypted_middle_name || '',
    //             encrypted_last_name: personal.encrypted_last_name,
    //             encrypted_username: personal.encrypted_username || '',
    //             gender: personal.gender || '',
    //             birthday: personal.birthday ? personal.birthday.split('T')[0] : '',
    //             encrypted_email_address: personal.encrypted_email_address || '',
    //             encrypted_phone: personal.encrypted_phone || '',
    //             encrypted_address_1: personal.encrypted_address_1 || '',
    //             encrypted_address_2: personal.encrypted_address_2 || '',
    //             encrypted_city_town: personal.encrypted_city_town || '',
    //             encrypted_state: personal.encrypted_state || '',
    //             encrypted_zip_postal_code: personal.encrypted_zip_postal_code || '',
    //             encrypted_country: personal.encrypted_country || '',
    //             encrypted_timezone: personal.encrypted_timezone || '',
    //             encrypted_notes: personal.encrypted_notes || '',
    //             category_id: personal.category_id || '',
    //             name: personal.name || '',
    //             favourite: personal.favourite || false,
    //             shared_key: personal.shared_key || ''
    //         });
    //         setSelectedOptions(initializeSelectedOptions());
    //     } else {
    //         // New personal details - preserve categoryId and shared info
    //         setFormData(prev => ({
    //             ...prev,
    //             category_id: categoryId || '',
    //             shared_key: sharedKey || ''
    //         }));
    //         setSelectedOptions(initializeSelectedOptions());
    //     }

    //     // if (isOpen && !isSharedPersonal && personal?.itemType !== 'category') {
    //     //     refreshCategories();
    //     // }
    // }, [isOpen, personal, categoryId, sharedKey]);

    useEffect(() => {
        if (isOpen) {
            const initialOptions = initializeSelectedOptions();
            setSelectedOptions(initialOptions);
            
            // Store initial categories for tracking removals
            if (personal?.categories && personal.categories.length > 0) {
                initialCategoriesRef.current = personal.categories.filter(personalCategory => 
                    categories.some((userCategory:any) => userCategory.id === personalCategory.id)
                ).map(personalCategory => personalCategory.id);
            }

            setFormData(prev => ({
                ...prev,
                name: personal?.name || '',
                category_id: personal?.category_id || categoryId || '',
                title: personal?.title || '',
                encrypted_first_name: personal?.encrypted_first_name || '',
                encrypted_middle_name: personal?.encrypted_middle_name || '',
                encrypted_last_name: personal?.encrypted_last_name || '',
                encrypted_username: personal?.encrypted_username || '',
                gender: personal?.gender || '',
                birthday: personal?.birthday || '',
                encrypted_email_address: personal?.encrypted_email_address || '',
                encrypted_phone: personal?.encrypted_phone || '',
                encrypted_address_1: personal?.encrypted_address_1 || '',
                encrypted_address_2: personal?.encrypted_address_2 || '',
                encrypted_city_town: personal?.encrypted_city_town || '',
                encrypted_state: personal?.encrypted_state || '',
                encrypted_zip_postal_code: personal?.encrypted_zip_postal_code || '',
                encrypted_country: personal?.encrypted_country || '',
                encrypted_timezone: personal?.encrypted_timezone || '',
                encrypted_notes: personal?.encrypted_notes || '',
                favourite: personal?.favourite || false,
                shared_key: personal?.shared_key || '',
                category_ids: initialOptions.map(opt => opt.value),
                remove_category_ids: []
            }));

            // if (!isSharedCard) {
            //     refreshCategories();
            // }
        }
    }, [isOpen, personal, categories, categoryId]);

    // Reset when modal closes
    useEffect(() => {
        if (!isOpen) {
            initialCategoriesRef.current = [];
            setSelectedOptions([]);
            setFormData(prev => ({
                ...prev,
                category_ids: [],
                remove_category_ids: []
            }));
        }
    }, [isOpen]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        const newValue = isCheckbox ? (e.target as HTMLInputElement).checked : value;

        if (name === 'category_id' && value) {
            const category = categories.find(cat => cat.id === value);
            setSelectedCategory(category);

            // If this is a shared category, update the shared fields
            if (category?.is_shared) {
                setFormData(prev => ({
                    ...prev,
                    category_id: value,
                    isSharedUpdate: true,
                    shared_key: category.shared_key ? decryptKeyData(category.shared_key) : ''
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    category_id: value,
                    isSharedUpdate: false,
                    shared_key: ''
                }));
            }
        }
        else if (name === "encrypted_phone") {
            let cleaned = value.replace(/[^0-9+\-() ]/g, '');
            // Limit to max 15 characters
            if (cleaned.length > 15) {
                cleaned = cleaned.slice(0, 15);
            }
            setFormData(prev => ({ ...prev, [name]: cleaned }));
        }
        else {

            // For other fields
            setFormData(prev => ({
                ...prev,
                [name]: newValue
            }));
        }
    };

    const handleMultiSelectChange = (selected: Option[]) => {
        const newSelectedIds = selected.map(opt => opt.value);
        
        // Compare with initial categories to determine which ones were removed
        const removedCategoryIds = initialCategoriesRef.current.filter(
            id => !newSelectedIds.includes(id)
        );

        console.log('All Removed Category IDs:', removedCategoryIds);

        setSelectedOptions(selected);
        
        // Check if any selected category is shared
        const hasSharedCategory = selected.some(opt => opt.isShared);
        const sharedOption = selected.find(opt => opt.isShared);
        
        console.log("hasSharedCategory", hasSharedCategory);
        // Determine the shared key value, ensuring it's always a string
        const newSharedKey = hasSharedCategory && sharedOption?.sharedKey ? 
            decryptKeyData(sharedOption.sharedKey) : 
            (isSharedPersonal ? personal?.shared_key || '' : '');

        setFormData(prev => ({
            ...prev,
            category_ids: newSelectedIds,
            remove_category_ids: removedCategoryIds,
            shared_key: newSharedKey
        }));
    };

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;

        setCreatingCategoryLoading(true);
        try {
            const newCategory = await createCategory({ name: newCategoryName, workspace_id: activeWorkspace?.id || undefined });
            await refreshCategories();

            // Find the newly created category and select it
            // const newCategory = categories.find(cat => cat.name === newCategoryName);
            // if (newCategory) {
            //     setFormData(prev => ({
            //         ...prev,
            //         category_id: newCategory.id
            //     }));
            // }

            const newOption: Option = {
                label: newCategoryName,
                value: newCategory.id,
                isShared: false,
                sharedKey: undefined
            };
            setSelectedOptions([...selectedOptions, newOption]);

            // Reset the new category UI state
            setNewCategoryName('');
            setIsCreatingCategory(false);
        } catch (error) {
            console.error('Error creating category:', error);
            toast.error('Failed to create category');
        } finally {
            setCreatingCategoryLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const workspaceId = activeWorkspace?.id || team_id || undefined;
        try {
            if (personal) {
                let isVaultOwner = false;
                if (personal?.shared_key) {
                    try {
                        isVaultOwner = personal.shared_key === secureStore.getVaultKey();
                    } catch (error) {
                        console.error('Error decrypting shared key:', error);
                    }
                }
                console.log("personal details before update", personal);
                console.log('form data', formData.shared_key);
                await PersonalDetailsService.updatePersonalDetail(personal.id, {
                    ...formData,
                    id: personal.id,
                    category_ids: selectedOptions.length > 0 ? selectedOptions.map(opt => opt.value) : [], // Set to [] if no options selected
                    remove_category_ids: formData.remove_category_ids,
                    favourite: formData.favourite,
                    isSharedUpdate: isVaultOwner ? isVaultOwner : isSharedPersonal,
                    shared_key: isVaultOwner ? personal.shared_key : formData.shared_key || personal.shared_key || sharedKey,
                    itemType: isVaultOwner || isSharedPersonal ? 'category' : personal.itemType || 'personal',
                    workspace_id: workspaceId
                });
                toast.success('Personal details updated successfully');
            } else {
                await PersonalDetailsService.createPersonalDetail({
                    ...formData,
                    category_ids: selectedOptions.map(opt => opt.value),
                    favourite: formData.favourite,
                    isSharedUpdate: selectedCategory?.is_shared || false,
                    shared_key: secureStore.getVaultKey(),
                    workspace_id: workspaceId
                });
                toast.success('Personal details created successfully');
            }
            if (onSuccessfulUpdate) {
                await onSuccessfulUpdate();
            } else {
                onSuccess();
            }
            onClose();
        } catch (error) {
            console.error('Error saving personal details:', error);
            toast.error(personal ? 'Failed to update personal details' : 'Failed to create personal details');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const isReadOnly = !personal?.is_owner && isSharedPersonal && personal?.permission_level === 'read' && !(role_id === 1 || role_id === 2);
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Enhanced Backdrop */}
            <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"></div>

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
                <div className="relative bg-white rounded-2xl sm:rounded-3xl w-full max-w-7xl mx-2 sm:mx-4 max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100">
                    {/* Modal Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 sm:px-8 py-4 sm:py-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-4">
                                <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                                    <IdentificationIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg sm:text-2xl font-bold">
                                        {isReadOnly ? 'View Personal Details' : (personal ? 'Edit Personal Details' : 'Create Personal Details')}
                                    </h3>
                                    <p className="text-primary-100 text-xs sm:text-sm hidden sm:block">
                                        {isReadOnly ? 'Review personal information' : 'Manage your personal information securely'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 sm:p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl sm:rounded-2xl transition-all duration-200"
                            >
                                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="flex flex-col h-full" autoComplete="off">
                        <div className="flex-1 overflow-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(95vh-140px)] bg-gray-50">
                            <div className="p-4 sm:p-8">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
                                    {/* Left Column - Basic Info & Vaults */}
                                    <div className="lg:col-span-1 order-1">
                                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 h-fit mb-4 lg:mb-0">
                                            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                                                <div className="p-1.5 sm:p-2 bg-primary-100 rounded-lg sm:rounded-xl">
                                                    <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
                                                </div>
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">Basic Details</h4>
                                            </div>

                                            <div className="space-y-4 sm:space-y-6">
                                                {/* Name */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Display Name
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            value={formData.name}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            required
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter display name"
                                                        />
                                                        <UserIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Vaults Section */}
                                                {isAdminView || (!isSharedPersonal || role_id === 1 || role_id === 2) && (
                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                                                            <FolderIcon className="inline w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                                            Vaults
                                                        </label>
                                                        <div className="space-y-2 sm:space-y-3">
                                                            <div className="text-sm">
                                                                <MultiSelect
                                                                    options={categoryOptions}
                                                                    value={selectedOptions}
                                                                    onChange={handleMultiSelectChange}
                                                                    labelledBy="Select vaults"
                                                                    disabled={isReadOnly}
                                                                    hasSelectAll={false}
                                                                    className="w-full"
                                                                    ClearSelectedIcon={null}
                                                                    debounceDuration={0}
                                                                />
                                                            </div>

                                                            {/* Create New Vault */}
                                                            {!isReadOnly && (
                                                                <div>
                                                                    {!isCreatingCategory ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setIsCreatingCategory(true)}
                                                                            className="w-full flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg sm:rounded-xl transition-all duration-200"
                                                                        >
                                                                            <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                                                            Create New Vault
                                                                        </button>
                                                                    ) : (
                                                                        <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
                                                                            <input
                                                                                type="text"
                                                                                value={newCategoryName}
                                                                                onChange={(e) => setNewCategoryName(e.target.value)}
                                                                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md sm:rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                                placeholder="Enter vault name"
                                                                                autoFocus
                                                                            />
                                                                            <div className="flex space-x-2">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={handleCreateCategory}
                                                                                    disabled={creatingCategoryLoading || !newCategoryName.trim()}
                                                                                    className={`flex-1 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                                                                                        creatingCategoryLoading || !newCategoryName.trim()
                                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                                        : 'bg-primary-600 text-white hover:bg-primary-700'
                                                                                    }`}
                                                                                >
                                                                                    {creatingCategoryLoading ? 'Creating...' : 'Create'}
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setIsCreatingCategory(false);
                                                                                        setNewCategoryName('');
                                                                                    }}
                                                                                    className="flex-1 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Favorite Toggle */}
                                                {!isAdminView && (
                                                <div className="flex items-center p-3 sm:p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg sm:rounded-xl border border-red-100">
                                                    <input
                                                        type="checkbox"
                                                        id="favourite"
                                                        name="favourite"
                                                        checked={formData.favourite}
                                                        onChange={(e) => setFormData({ ...formData, favourite: e.target.checked })}
                                                        className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 focus:ring-red-400 border-red-300 rounded"
                                                        disabled={isReadOnly}
                                                    />
                                                    <label htmlFor="favourite" className="ml-2 sm:ml-3 flex items-center text-xs sm:text-sm font-medium text-red-700">
                                                        {formData.favourite ? (
                                                            <HeartSolidIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mr-1 sm:mr-2" />
                                                        ) : (
                                                            <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 mr-1 sm:mr-2" />
                                                        )}
                                                        Add to favorites
                                                    </label>
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Middle Column - Personal Information */}
                                    <div className="lg:col-span-1 order-2">
                                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 h-fit mb-4 lg:mb-0">
                                            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                                                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg sm:rounded-xl">
                                                    <UserGroupIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                                </div>
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">Personal Information</h4>
                                            </div>

                                            <div className="space-y-4 sm:space-y-6">
                                                {/* Title and Gender Row */}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            Title
                                                        </label>
                                                        <div className="relative">
                                                            <select
                                                                name="title"
                                                                value={formData.title}
                                                                onChange={handleChange}
                                                                disabled={isReadOnly}
                                                                required
                                                                className={`w-full pl-7 sm:pl-10 pr-2 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                    isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                                } border-gray-300`}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="Mr">Mr</option>
                                                                <option value="Mrs">Mrs</option>
                                                                <option value="Ms">Ms</option>
                                                                <option value="Dr">Dr</option>
                                                            </select>
                                                            <IdentificationIcon className="absolute left-1.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            Gender
                                                        </label>
                                                        <div className="relative">
                                                            <select
                                                                name="gender"
                                                                value={formData.gender}
                                                                onChange={handleChange}
                                                                disabled={isReadOnly}
                                                                className={`w-full pl-7 sm:pl-10 pr-2 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                    isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                                } border-gray-300`}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="Male">Male</option>
                                                                <option value="Female">Female</option>
                                                                <option value="Other">Other</option>
                                                            </select>
                                                            <UserIcon className="absolute left-1.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* First Name */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        First Name
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_first_name"
                                                            value={formData.encrypted_first_name}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            required
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter first name"
                                                        />
                                                        <UserIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Middle Name */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Middle Name
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_middle_name"
                                                            value={formData.encrypted_middle_name}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter middle name (optional)"
                                                        />
                                                        <UserIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Last Name */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Last Name
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_last_name"
                                                            value={formData.encrypted_last_name}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            required
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter last name"
                                                        />
                                                        <UserIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Birthday */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Birthday
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="date"
                                                            name="birthday"
                                                            value={formData.birthday}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                        />
                                                        <CalendarIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Contact Info */}
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            Email Address
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                type="email"
                                                                name="encrypted_email_address"
                                                                value={formData.encrypted_email_address}
                                                                onChange={handleChange}
                                                                disabled={isReadOnly}
                                                                className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                    isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                                } border-gray-300`}
                                                                placeholder="Enter email address"
                                                            />
                                                            <EnvelopeIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            Phone Number
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                type="tel"
                                                                name="encrypted_phone"
                                                                value={formData.encrypted_phone}
                                                                onChange={handleChange}
                                                                disabled={isReadOnly}
                                                                inputMode="tel"
                                                                className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                    isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                                } border-gray-300`}
                                                                placeholder="Enter phone number"
                                                            />
                                                            <PhoneIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Address & Additional Info */}
                                    <div className="lg:col-span-1 order-3">
                                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 h-fit">
                                            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                                                <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg sm:rounded-xl">
                                                    <HomeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                                </div>
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">Address & Notes</h4>
                                            </div>

                                            <div className="space-y-4 sm:space-y-6">
                                                {/* Address Line 1 */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Address Line 1
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_address_1"
                                                            value={formData.encrypted_address_1}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter street address"
                                                        />
                                                        <MapPinIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Address Line 2 */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Address Line 2
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_address_2"
                                                            value={formData.encrypted_address_2}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Apartment, suite, etc. (optional)"
                                                        />
                                                        <MapPinIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* City and State */}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            City/Town
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="encrypted_city_town"
                                                            value={formData.encrypted_city_town}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="City"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            State/Province
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="encrypted_state"
                                                            value={formData.encrypted_state}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="State"
                                                        />
                                                    </div>
                                                </div>

                                                {/* ZIP and Country */}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            ZIP/Postal Code
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="encrypted_zip_postal_code"
                                                            value={formData.encrypted_zip_postal_code}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="ZIP"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            Country
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="encrypted_country"
                                                            value={formData.encrypted_country}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Country"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Notes */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Notes
                                                    </label>
                                                    <textarea
                                                        name="encrypted_notes"
                                                        value={formData.encrypted_notes}
                                                        onChange={handleChange}
                                                        rows={6}
                                                        disabled={isReadOnly}
                                                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none ${
                                                            isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                        }`}
                                                        placeholder="Add any additional notes about this person..."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Footer */}
                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-8 py-4 sm:py-6">
                            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
                                    <ShieldCheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                                    <span>All personal data is encrypted and secure</span>
                                </div>
                                <div className="flex space-x-3 sm:space-x-4 w-full sm:w-auto order-1 sm:order-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200"
                                    >
                                        {isReadOnly ? 'Close' : 'Cancel'}
                                    </button>
                                    {isAdminView || !isReadOnly && (
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className={`flex-1 sm:flex-none px-6 sm:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-white transition-all duration-200 ${
                                                loading
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl'
                                            }`}
                                        >
                                            {loading ? (
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-2"></div>
                                                    Saving...
                                                </div>
                                            ) : (
                                                personal ? 'Update Personal Details' : 'Create Personal Details'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PersonalDetailsModal; 